import React, { useEffect, useMemo, useRef, useState } from "react";
import "./css/gallery.css";
import { Filters, applyFilters, createDefaultFilters } from "./Filters";

// A lightweight React wrapper around Swiper's CDN build.
// Loads Bootstrap, Bootstrap Icons, and Swiper styles/scripts on mount.
const SwiperGallery = () => {
  const swiperId = useMemo(
    () => `swiper-${Math.random().toString(36).slice(2, 8)}`,
    [],
  );
  const nextId = `${swiperId}-next`;
  const prevId = `${swiperId}-prev`;

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewMode] = useState("grid"); // 'carousel' | 'grid'
  const [filters, setFilters] = useState(createDefaultFilters());
  const [showFilters, setShowFilters] = useState(false);

  const pageSize = 12;
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const loadMoreRef = useRef(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreTimeoutRef = useRef(null);

  const toFiniteNumber = (value) => {
    if (value == null) return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const shuffleArray = (arr) => {
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  };

  const pluralize = (count, one, many) => {
    if (count === 1) return one;
    return many;
  };

  const countLabel = (value, one, many) => {
    const count = toFiniteNumber(value);
    if (!count || count <= 0) return "לא צוין";
    return `${count} ${pluralize(count, one, many)}`;
  };

  const cityLabel = (city) => {
    if (city == null) return "";
    const text = String(city).trim();
    if (!text) return text;
    const containsHebrew = /[\u0590-\u05FF]/.test(text);
    if (containsHebrew) return text;
    const map = { eilat: "אילת", israel: "ישראל" };
    return text
      .split(",")
      .map((part) => {
        const trimmed = part.trim();
        return map[trimmed.toLowerCase()] || trimmed;
      })
      .join(", ");
  };

  const titleLabel = (value) => {
    if (value == null) return "";
    const text = String(value).trim();
    if (!text) return text;
    return text.replace(/\s*-\s*by\s+edom\s*$/i, " - מאת Edom");
  };

  const handleCheckOffer = (item) => {
    if (item?.id) {
      const target = `/listing/?listingId=${encodeURIComponent(item.id)}`;
      window.location.href = target;
    } else {
      console.warn("Missing listing id", item);
    }
  };

  useEffect(() => {
    fetch("https://seasideeilat.co.il/wp-json/guesty/v1/listings")
      .then((res) => res.json())
      .then((data) => {
        console.log("Fetched listings:", data);
        if (data?.listings?.length) {
          const mapped = shuffleArray(data.listings).map((item) => {
            const basePrice = Number(item?.prices?.basePrice);
            const priceIsValid = Number.isFinite(basePrice);
            return {
              id: item._id,
              title: item.title || "דירה",
              city: `${item.address?.city || ""}${
                item.address?.country ? `, ${item.address.country}` : ""
              }`.trim(),
              image:
                item.picture?.original || item.pictures?.[0]?.original || "",
              guests: item.accommodates || 0,
              bedrooms: item.bedrooms ?? 0,
              beds: item.beds ?? item.bedsCount ?? item.bedCount ?? 0,
              bathrooms: item.bathrooms ?? 0,
              amenities: item.amenities || [],
              prices: { basePrice: priceIsValid ? basePrice : Infinity },
              priceLabel: priceIsValid ? `₪${basePrice}` : "₪—",
            };
          });
          setListings(mapped);
          setVisibleCount(pageSize);
          setLoadingMore(false);
          setError("");
          setLoading(false);
        } else {
          setError("לא נמצאו דירות מה-API");
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch listings", err);
        setError("שגיאה בטעינת הדירות");
        setLoading(false);
      });
  }, [nextId, prevId, swiperId]);

  const filteredListings = useMemo(
    () => applyFilters(listings, filters),
    [listings, filters],
  );
  const canLoadMore = visibleCount < filteredListings.length;
  const visibleListings = useMemo(
    () => filteredListings.slice(0, visibleCount),
    [filteredListings, visibleCount],
  );

  useEffect(() => {
    setVisibleCount(pageSize);
    setLoadingMore(false);
  }, [filters]);

  useEffect(() => {
    if (viewMode !== "grid") return;
    if (loading || error) return;
    if (!canLoadMore) return;

    const node = loadMoreRef.current;
    if (!node) return;
    if (!("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;
        if (loadingMore) return;
        setLoadingMore(true);
        if (loadMoreTimeoutRef.current) {
          clearTimeout(loadMoreTimeoutRef.current);
        }
        loadMoreTimeoutRef.current = setTimeout(() => {
          setVisibleCount((prev) =>
            Math.min(prev + pageSize, filteredListings.length),
          );
          setLoadingMore(false);
        }, 750);
      },
      { root: null, rootMargin: "300px 0px", threshold: 0 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [
    viewMode,
    loading,
    error,
    canLoadMore,
    filteredListings.length,
    loadingMore,
  ]);

  useEffect(() => {
    return () => {
      if (loadMoreTimeoutRef.current) {
        clearTimeout(loadMoreTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (viewMode !== "carousel") return;

    const initSwiper = () => {
      const swiperEl = document.querySelector(`#${swiperId}`);
      if (swiperEl && swiperEl.swiper) {
        swiperEl.swiper.destroy(true, true);
      }
      // eslint-disable-next-line no-undef
      new Swiper(`#${swiperId}`, {
        slidesPerView: 3,
        spaceBetween: 4,
        loop: true,
        grabCursor: true,
        navigation: {
          nextEl: `#${nextId}`,
          prevEl: `#${prevId}`,
        },
        breakpoints: {
          0: { slidesPerView: 1.05, spaceBetween: 12 },
          576: { slidesPerView: 1.4, spaceBetween: 14 },
          768: { slidesPerView: 2, spaceBetween: 14 },
          992: { slidesPerView: 3, spaceBetween: 14 },
        },
      });
    };

    if (!listings.length) return;
    if (!window.Swiper) {
      const poll = setInterval(() => {
        if (window.Swiper) {
          clearInterval(poll);
          initSwiper();
        }
      }, 100);
      return () => clearInterval(poll);
    }

    initSwiper();
  }, [listings, nextId, prevId, swiperId, viewMode]);

  const renderCard = (item) => (
    <div className=" listing-card shadow-sm">
      <img
        src={item.image}
        className="card-img-top"
        alt={titleLabel(item.title)}
        loading="lazy"
      />
      <div className="listing-body">
        <div className="listing-title overflow-fade">
          {titleLabel(item.title)}
        </div>
        <div className="listing-meta">
          <i className="bi bi-geo-alt" />
          <span>{cityLabel(item.city)}</span>
        </div>
        <div className="listing-amenities">
          <div className="amenity">
            <i className="bi bi-cloud-moon" />
            <span>{countLabel(item.beds, "מיטה", "מיטות")}</span>
          </div>
          <div className="amenity">
            <i className="bi bi-people" />
            <span>{countLabel(item.guests, "אורח", "אורחים")}</span>
          </div>
          <div className="amenity">
            <i className="bi bi-door-open" />
            <span>{countLabel(item.bedrooms, "חדר שינה", "חדרי שינה")}</span>
          </div>
          <div className="amenity">
            <i className="bi bi-badge-wc" />
            <span>{countLabel(item.bathrooms, "חדר רחצה", "חדרי רחצה")}</span>
          </div>
        </div>
        {/* <div className="price-block">
          <span className="label">מחיר ללילה</span>
          <span className="price">{item.priceLabel}</span>
          <div className="fine-print">ייתכן שיחולו חיובים נוספים.</div>
        </div> */}
      </div>
      <button
        type="button"
        className="btn bg-black text-white m-2"
        onClick={() => handleCheckOffer(item)}
      >
        לבחירת תאריכים{" "}
      </button>
    </div>
  );

  return (
    <div className="gallery-shell" dir="rtl">
      {/* <div className="gallery-top">
        <div className="view-toggle" role="group" aria-label="בחר תצוגה">
          <button
            type="button"
            className={viewMode === "carousel" ? "active" : ""}
            onClick={() => setViewMode("carousel")}
          >
            קרוסלה
          </button>
          <button
            type="button"
            className={viewMode === "grid" ? "active" : ""}
            onClick={() => setViewMode("grid")}
          >
            גריד
          </button>
        </div>
      </div> */}
      <div className="carousel-wrapper pt-3 pb-5">
        <div className="carousel-frame">
          {loading && (
            <div className="loader-wrap">
              <div className="lds-dual-ring"></div>
            </div>
          )}
          {error && !loading && (
            <div style={{ padding: "12px", color: "red", textAlign: "center" }}>
              {error}
            </div>
          )}
          {!loading && !error && !listings.length && (
            <div style={{ padding: "12px", textAlign: "center" }}>
              לא נמצאו דירות להצגה
            </div>
          )}
          {viewMode === "carousel" && (
            <>
              <button
                className="nav-btn nav-prev swiper-button-prev"
                id={prevId}
                aria-label="Previous slide"
              >
                <i className="bi bi-chevron-left text-black" />
              </button>
              <div className="gallery-row">
                <div className="swiper listing-swiper" id={swiperId}>
                  <div className="swiper-wrapper">
                    {listings.map((item) => (
                      <div className="swiper-slide" key={item.id || item.title}>
                        {renderCard(item)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <button
                className="nav-btn nav-next swiper-button-next"
                id={nextId}
                aria-label="Next slide"
              >
                <i className="bi bi-chevron-right text-black" />
              </button>
            </>
          )}
          {viewMode === "grid" && !loading && !error && (
            <div className="grid-wrapper">
              <div className="filters-bar">
                <button
                  type="button"
                  className={`filter-toggle ${showFilters ? "active" : ""}`}
                  onClick={() => setShowFilters((s) => !s)}
                >
                  סינון
                  <i className="bi bi-sliders2-vertical" />
                </button>
                <div className="results-count">
                  מציג {visibleListings.length} מתוך {filteredListings.length}{" "}
                  תוצאות
                </div>
              </div>
              {showFilters && (
                <div className="filters-card">
                  <Filters value={filters} onChange={setFilters} />
                </div>
              )}
              <div className="listing-grid">
                {visibleListings.map((item) => (
                  <div className="grid-cell" key={item.id || item.title}>
                    {renderCard(item)}
                  </div>
                ))}
              </div>
              {!loading && !error && (
                <div className="infinite-footer">
                  {canLoadMore ? (
                    <>
                      <div ref={loadMoreRef} className="infinite-sentinel" />
                      {loadingMore && (
                        <div className="infinite-loader" aria-live="polite">
                          <div className="lds-dual-ring lds-dual-ring--sm" />
                          <div className="infinite-loader-text">
                            טוען עוד דירות...
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="infinite-done">הוצגו כל התוצאות</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SwiperGallery;
